import type {
  CoachingGoal,
  CoachingGoalMilestone,
  CoachingGoalReflection,
  CoachingPlan,
} from '@efdigital/ll-addon-data';
import { queryCoachingGoalMilestonesByPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goal-milestones';
import { queryCoachingGoalReflectionsByPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goal-reflections';
import { queryCoachingGoalsByPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goals';
import type {
  CoacheeEngagement,
  CoachingCoachCoacheesCoacheeModel,
} from '@efdigital/ll-xceed-v2/routes/coaching/coach/coachees/coachee';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

export type CoachingCoachCoacheesCoacheeGoalsModel =
  CoachingCoachCoacheesCoacheeModel & {
    goals: CoachingGoal[];
    milestones: CoachingGoalMilestone[];
    reflections: CoachingGoalReflection[];
    /** Plan the composer writes against — the only engagement, or the first. */
    defaultPlan: CoachingPlan | null;
  };

export default class CoachingCoachCoacheesCoacheeGoalsRoute extends Route<CoachingCoachCoacheesCoacheeGoalsModel> {
  @service declare store: Store;

  async model(): Promise<CoachingCoachCoacheesCoacheeGoalsModel> {
    const parent = this.modelFor(
      'coaching.coach.coachees.coachee'
    ) as CoachingCoachCoacheesCoacheeModel;

    const planIds = [
      ...new Set(
        parent.engagements
          .map((engagement: CoacheeEngagement) => engagement.coachingPlan.id)
          .filter((id): id is string => Boolean(id))
      ),
    ];
    const pages = await Promise.all(
      planIds.map(async (planId) => {
        const [goals, milestones, reflections] = await Promise.all([
          this.store.request(queryCoachingGoalsByPlan(planId)),
          this.store.request(queryCoachingGoalMilestonesByPlan(planId)),
          this.store.request(queryCoachingGoalReflectionsByPlan(planId)),
        ]);

        return { goals, milestones, reflections };
      })
    );

    return {
      ...parent,
      goals: pages.flatMap((page) => page.goals.content.data ?? []),
      milestones: pages.flatMap((page) => page.milestones.content.data ?? []),
      reflections: pages.flatMap((page) => page.reflections.content.data ?? []),
      defaultPlan: parent.engagements[0]?.coachingPlan ?? null,
    };
  }
}
